import type { ChatMessage, ChatPart } from "../types"

const computeMessageComplete = (
  parts: ChatPart[],
  role: ChatMessage["role"]
): boolean => {
  if (role === "user") return true
  const appendParts = parts.filter(part => part.chunk_mode === "append")
  if (appendParts.length === 0) return true
  return appendParts.every(part => part.final === true)
}

export function computeMessages(
  allParts: Record<string, ChatPart>,
  sessionId: string | null
): ChatMessage[] {
  if (!sessionId) return []

  const currentSessionParts = Object.values(allParts)
    .filter(p => p.session_id === sessionId)
    .sort((a, b) => {
      const tA = new Date(a.timestamp).getTime()
      const tB = new Date(b.timestamp).getTime()
      return tA === tB ? (a.index || 0) - (b.index || 0) : tA - tB
    })

  const msgs: ChatMessage[] = []
  let currentMsg: ChatMessage | null = null

  for (const part of currentSessionParts) {
    const partIsUser = part.role === "user" || part.type === "user_message"
    const role = partIsUser ? "user" : "assistant"

    const crossesMessageBoundary =
      currentMsg?.parts?.[0]?.message_id != null &&
      part.message_id != null &&
      currentMsg.parts[0].message_id !== part.message_id

    if (
      currentMsg !== null &&
      currentMsg.role === role &&
      !crossesMessageBoundary
    ) {
      // Accumulate into the current message group.
      currentMsg.parts ??= []
      currentMsg.parts.push(part)
      if (part.type === "text") {
        currentMsg.content += part.content
      }
      currentMsg.isComplete = computeMessageComplete(
        currentMsg.parts,
        currentMsg.role
      )
      currentMsg.timestamp = part.timestamp
      continue
    }

    // Start a new message group.
    if (currentMsg !== null) msgs.push(currentMsg)

    const showContent = part.type === "text"

    currentMsg = {
      id:
        part.message_id ||
        `msg-${part.timestamp}-${Math.random().toString(36).substring(7)}`,
      role,
      content: showContent ? part.content : "",
      parts: [part],
      timestamp: part.timestamp,
      isComplete: computeMessageComplete([part], role),
    }
  }

  if (currentMsg) msgs.push(currentMsg)

  return msgs
}
